import { Dispatch, KeyboardEvent, MouseEvent, SetStateAction, useCallback, useRef } from 'react';
import SearchIcon from '../../../svg/common/search';
import { ICountry } from '../../../../domain/addresses/interfaces';

interface IPhoneFieldAutocomplete {
    filteredCountries: ICountry[],
    selectCountry: Function,
    filter: Function,
    selectedCountryID: number,
    setSelectedCountryID: Dispatch<SetStateAction<number>>,
}

/**
 * Phone Field Autocomplete
 */
const PhoneFieldAutocomplete = (props: IPhoneFieldAutocomplete) => {

    // contains dropdown items
    // used to focus on the next item after 'down' / 'up' arrow is pressed
    // focus will scroll the div content automatically to the chosen option
    const countriesRef = useRef<Array<HTMLElement | null>>([]);

    const { filteredCountries, selectedCountryID, setSelectedCountryID } = props;

    /**
     * filter countries
     */
    const autocompleteItemKeyUp = (evt: KeyboardEvent) => {
        const $target = evt.target as HTMLFormElement;
        props.filter($target.value);
    };

    /**
     * handle 'up' & 'down' arrows
     */
    const handleArrows = useCallback((direction: string) => {

        const count = filteredCountries.length;
        if(count <= 0) return;

        const selectedCountryIndex = filteredCountries.findIndex(country => country.countryID === selectedCountryID);
        if(selectedCountryIndex === -1) return;

        // find the index of the next / prev country
        let newIndex =  direction === 'down' ? selectedCountryIndex + 1 : selectedCountryIndex - 1;
        newIndex = (newIndex % count + count) % count;

        // update selected country id in the state
        const newCountry = filteredCountries[newIndex];
        setSelectedCountryID(newCountry.countryID);
        const $item = countriesRef.current[newCountry.countryID];
        $item?.focus();

    }, [filteredCountries, selectedCountryID, setSelectedCountryID]);

    /**
     * handle arrow up / down, and enter
     */
    const autocompleteItemKeyDown = (evt: KeyboardEvent, countryID?: number) => {
        if(evt.key === 'ArrowDown') {
            evt.preventDefault();
            evt.stopPropagation();
            handleArrows('down');
        }

        if(evt.key === 'ArrowUp') {
            evt.preventDefault();
            evt.stopPropagation();
            handleArrows('up');
        }

        if(countryID && (evt.key === 'Enter' || evt.key === ' ')) {
            props.selectCountry(countryID);
        }
    };

    /**
     * prevent drop-down closing on filter text box click
     */
    const handleTextBoxClick = (evt: MouseEvent) => {
        evt.stopPropagation();
    };

    return (
        <div className="w-form-phone-field__autocomplete bg-white whitespace-nowrap absolute">

            {/* filter */}
            <div className="flex items-center pr-2 border-b">
                <input
                    aria-label="phone"
                    autoComplete="off"
                    className="flex-1 px-2 py-1 mr-2 focus:outline-none"
                    placeholder="Search"
                    type="text"
                    onClick={ handleTextBoxClick }
                    onKeyUp={ autocompleteItemKeyUp }
                    onKeyDown={ (evt) => {
                        autocompleteItemKeyDown(evt, selectedCountryID);
                    } }
                    autoFocus
                />
                <SearchIcon width="13px" color="#252525" title="Filter" />
            </div>

            {/* drop-down */}
            <div
                role="listbox"
                className="flex flex-col w-full overflow-y-auto"
                tabIndex={ -1 }
                style={{
                    maxHeight: '250px',
                    overflowX: 'hidden',
            }}>
                {
                    props.filteredCountries.map((country) => {

                        const isSelected = country.countryID === selectedCountryID;

                        return (
                            <button
                                key={`filtered-country-${ country.countryID }`}
                                className={`text-sm md:text-base w-full border-b text-left py-1 pointer hover:bg-gray-150 ${ isSelected ? 'bg-gray-150' : '' } focus:outline-none`}
                                onClick={ () => {
                                    props.selectCountry(country.countryID);
                                }}
                                onKeyDown={ (evt) => {
                                    autocompleteItemKeyDown(evt, country.countryID);
                                }}
                                ref={el => countriesRef.current[country.countryID] = el}
                                tabIndex={ 0 }
                                type="button">
                                <div className="px-2 ">
                                    <span className={`w-form-phone__country-flag inline-block flag-icon flag-icon__${ country.country2Digit.toLowerCase() }`}></span>
                                    <span className="mx-2">{ country.countryDisplayName }</span>
                                    <span>(+{ country.phoneCode })</span>
                                </div>
                            </button>
                        )
                    })
                }
            </div>
        </div>
    )
};

export default PhoneFieldAutocomplete;